import { AppId } from '@lumapps/constants/app';
import type { Journey } from '@lumapps/journeys/types';
import { createPageRoute, type Route } from '@lumapps/router';

import { EndUserJourneyItemsListFilters, END_USER_JOURNEY_ITEMS_SORTING } from './types';

const endUserJourneyRouteSlug = 'journeys';

export const endUserJourneysBaseRoute: Route = createPageRoute({
    appId: AppId.frontOffice,
    slug: endUserJourneyRouteSlug,
});

// Journeys List
export const endUserJourneysListRoute: Route = createPageRoute({
    appId: AppId.frontOffice,
    slug: `${endUserJourneyRouteSlug}/list`,
});

export const endUserJourneysList = (): Route => ({
    ...endUserJourneysListRoute,
    params: {},
});

// Journeys as stakeholder
export const endUserJourneysAsStakeholderRoute: Route = createPageRoute({
    appId: AppId.frontOffice,
    slug: `${endUserJourneyRouteSlug}/items-as-stakeholder`,
});

export interface EndUserJourneysAsStakeholderQueryParams {
    isDone?: boolean;
    title?: string;
    sorting: END_USER_JOURNEY_ITEMS_SORTING;
    acknowledgeItemId?: string;
    journeys?: Journey[];
}

export const endUserJourneysAsStakeholder = (query?: EndUserJourneysAsStakeholderQueryParams): Route => {
    const queryParams: Record<string, string> = {};

    if (query?.title) {
        queryParams.title = query.title;
    }
    if (query?.isDone != null) {
        queryParams.isDone = String(query.isDone);
    }
    if (query?.sorting) {
        queryParams.sorting = query.sorting;
    }
    if (query?.journeys?.length) {
        queryParams.journeyIds = query.journeys.map(({ id }) => id).join(',');
    }

    // not an actual filter but the param to mark possible acknowledgment from url/email
    if (query?.acknowledgeItemId) {
        queryParams.acknowledgeItemId = query.acknowledgeItemId;
    }

    return {
        ...endUserJourneysAsStakeholderRoute,
        query: queryParams,
        params: {},
    };
};

// Enrollment View (= 1 enrollment + its items)
export const endUserJourneysViewRoute: Route = createPageRoute({
    appId: AppId.frontOffice,
    slug: `${endUserJourneyRouteSlug}/:enrollmentId`,
});

export interface EndUserJourneysViewParams {
    enrollmentId: string;
}
export interface EndUserJourneysViewQueryParams extends EndUserJourneyItemsListFilters {
    sorting: END_USER_JOURNEY_ITEMS_SORTING;
    acknowledgeItemId?: string;
}

export const endUserJourneysView = (
    params: EndUserJourneysViewParams,
    query?: EndUserJourneysViewQueryParams,
): Route => {
    const queryParams: Record<string, string> = {};

    if (query?.title) {
        queryParams.title = query.title;
    }
    if (query?.isDone != null) {
        queryParams.isDone = String(query.isDone);
    }
    if (query?.isAcknowledgeRequired != null) {
        queryParams.isAcknowledgeRequired = String(query.isAcknowledgeRequired);
    }
    if (query?.sorting) {
        queryParams.sorting = query.sorting;
    }

    // not an actual filter but the param to mark possible acknowledgment from url/email
    if (query?.acknowledgeItemId) {
        queryParams.acknowledgeItemId = query.acknowledgeItemId;
    }

    return {
        ...endUserJourneysViewRoute,
        query: queryParams,
        params: {
            enrollmentId: params.enrollmentId,
        },
    };
};

// Journey message with rich text
export const endUserJourneyItemViewRoute: Route = createPageRoute({
    appId: AppId.frontOffice,
    slug: `${endUserJourneyRouteSlug}/:enrollmentId/items/:enrollmentItemId`,
});

export interface EndUserJourneyItemViewParams extends EndUserJourneysViewParams {
    enrollmentItemId: string;
}

export const endUserJourneyItemView = (params: EndUserJourneyItemViewParams) => {
    return {
        ...endUserJourneyItemViewRoute,
        params: {
            enrollmentItemId: params.enrollmentItemId,
            enrollmentId: params.enrollmentId,
        },
    };
};
